import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ArrowLeft, ArrowRight, Search, Globe, CalendarDays } from "lucide-react";
import { EventCard3D } from "@/components/events/EventCard3D";
import { Breadcrumb } from "@/components/ui/breadcrumb-nav";

interface ImportedEvent {
  id: string;
  source_name: string;
  title: string;
  title_ar: string | null;
  description: string | null;
  description_ar: string | null;
  date: string | null;
  location: string | null;
  location_ar: string | null;
  image_url: string | null;
  category: string | null;
  external_link: string | null;
  scraped_at: string;
  ticket_status: string | null;
}

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isPast: boolean;
}

const parseEventDate = (dateStr: string | null): Date | null => {
  if (!dateStr) return null;
  const arabicNums: { [key: string]: string } = { '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4', '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9' };
  const arabicMonths: { [key: string]: number } = {
    'يناير': 0, 'فبراير': 1, 'مارس': 2, 'أبريل': 3, 'إبريل': 3,
    'مايو': 4, 'يونيو': 5, 'يوليو': 6, 'أغسطس': 7,
    'سبتمبر': 8, 'أكتوبر': 9, 'نوفمبر': 10, 'ديسمبر': 11
  };
  
  let converted = dateStr;
  Object.entries(arabicNums).forEach(([ar, en]) => {
    converted = converted.replace(new RegExp(ar, 'g'), en);
  });
  
  const parts = converted.split(' ').filter(p => p.trim());
  if (parts.length >= 3) {
    const day = parseInt(parts[0]);
    const month = arabicMonths[parts[1]];
    const year = parseInt(parts[2]);
    if (!isNaN(day) && month !== undefined && !isNaN(year)) {
      return new Date(year, month, day);
    }
  }
  
  const parsed = new Date(dateStr);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const calculateCountdown = (targetDate: Date): CountdownTime => {
  const now = new Date();
  const diff = targetDate.getTime() - now.getTime();
  const isPast = diff <= 0;
  
  const absDiff = Math.abs(diff);
  const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((absDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((absDiff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((absDiff % (1000 * 60)) / 1000);
  
  return { days, hours, minutes, seconds, isPast };
};

const useCountdown = (date: string | null) => {
  const [countdown, setCountdown] = useState<CountdownTime | null>(null);
  
  useEffect(() => {
    const eventDate = parseEventDate(date);
    if (!eventDate) return;
    
    const updateCountdown = () => setCountdown(calculateCountdown(eventDate));
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [date]);
  
  return countdown;
};

const EventCardWrapper = ({ event, isRTL }: { event: ImportedEvent; isRTL: boolean }) => {
  const countdown = useCountdown(event.date);
  return <EventCard3D event={event} isRTL={isRTL} countdown={countdown} />;
};

const ImportedEventsPage = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const navigate = useNavigate();
  const [events, setEvents] = useState<ImportedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    const { data, error } = await supabase
      .from("scraped_events")
      .select("*")
      .eq("is_published", true)
      .eq("category", "events")
      .order("scraped_at", { ascending: false });

    if (!error && data) {
      setEvents(data);
    }
    setLoading(false);
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = searchQuery === "" || 
      event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.title_ar?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesSearch;
  });

  return (
    <div className={`min-h-screen bg-gray-100 ${isRTL ? "rtl" : "ltr"}`}>
      <nav className="border-b bg-white w-full sticky top-0 z-10" style={{
        boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
      }}>
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="flex items-center gap-2"
            >
              {isRTL ? (
                <>
                  العودة للرئيسية
                  <ArrowRight className="w-4 h-4" />
                </>
              ) : (
                <>
                  <ArrowLeft className="w-4 h-4" />
                  Back to Home
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => i18n.changeLanguage(i18n.language === "en" ? "ar" : "en")}
              className="rounded-full"
            >
              <Globe className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb Navigation */}
        <Breadcrumb />

        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="p-3 rounded-xl" style={{
            background: 'linear-gradient(145deg, #ffffff, #e6e6e6)',
            boxShadow: '5px 5px 15px #d1d1d1, -5px -5px 15px #ffffff'
          }}>
            <CalendarDays className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-gray-700">
            {isRTL ? "جميع الفعاليات" : "All Events"}
          </h1>
        </div>

        {/* Search - Neumorphic */}
        <div className="flex gap-4 mb-8 max-w-xl mx-auto">
          <div className="relative flex-1 rounded-2xl" style={{
            background: 'linear-gradient(145deg, #f5f5f5, #ffffff)',
            boxShadow: 'inset 3px 3px 8px #e0e0e0, inset -3px -3px 8px #ffffff'
          }}>
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="search"
              placeholder={isRTL ? "البحث في الفعاليات..." : "Search events..."}
              className="pl-12 border-0 bg-transparent h-12 focus-visible:ring-0 focus-visible:ring-offset-0"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="rounded-2xl p-8 text-center" style={{
            background: 'linear-gradient(145deg, #f0f0f0, #ffffff)',
            boxShadow: '8px 8px 20px #d1d1d1, -8px -8px 20px #ffffff'
          }}>
            <CalendarDays className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-600 mb-2">
              {isRTL ? "لا توجد فعاليات" : "No events found"}
            </h2>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {filteredEvents.map((event) => (
              <EventCardWrapper key={event.id} event={event} isRTL={isRTL} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ImportedEventsPage;
